import { useEffect, useRef, useState } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import {
  Star,
  QrCode,
  Nfc,
  Sticker,
  CalendarCheck,
  DoorOpen,
  Store,
  Check,
  Info,
  type LucideIcon,
} from 'lucide-react'
import { MarketingNav } from '../components/MarketingNav'
import { MarketingFooter } from '../components/MarketingFooter'
import { useWindowWidth } from '../hooks/useWindowWidth'

// ─── Produkte (statisch, Bestellung kommt später) ────────────────────────────

interface Product {
  id: string
  name: string
  category: string
  description: string
  points: string[]
  price: string
  priceNote: string
  icon: LucideIcon
  glow: string
  badge?: string
}

const PRODUCTS: Product[] = [
  {
    id: 'google-review-card',
    name: 'Google Review Card',
    category: 'Bewertungen',
    description: 'Karte ans Handy halten oder QR-Code scannen – und der Kunde landet direkt bei deiner Google-Bewertung.',
    points: ['NFC-Chip und QR-Code', 'Kein App-Download nötig', 'Mit deinem Logo bedruckt'],
    price: 'ab 19 €',
    priceNote: 'pro Karte',
    icon: Star,
    glow: '#FF6D00',
    badge: 'Beliebt',
  },
  {
    id: 'bewertungs-aufsteller',
    name: 'Bewertungs-Aufsteller',
    category: 'Bewertungen',
    description: 'Für Tresen, Tisch oder Wartebereich. Fragt nach dem Besuch freundlich nach fünf Sternen.',
    points: ['Acryl, stabil und abwischbar', 'NFC und QR-Code', 'Hoch- oder Querformat'],
    price: 'ab 39 €',
    priceNote: 'pro Stück',
    icon: Store,
    glow: '#00B0FF',
  },
  {
    id: 'schaufenster-sticker',
    name: 'Schaufenster-Sticker',
    category: 'Online buchen',
    description: '„Jetzt online Termin buchen“ – mit QR-Code zu deinem Candlear-Buchungswidget. Auch nach Ladenschluss.',
    points: ['Für innen und außen', 'Rückstandsfrei ablösbar', 'Rund oder eckig'],
    price: 'ab 15 €',
    priceNote: 'pro Sticker',
    icon: Sticker,
    glow: '#4CAF50',
  },
  {
    id: 'terminkarten',
    name: 'Terminkarten',
    category: 'Stammkunden',
    description: 'Der nächste Termin steht vorne drauf, der QR-Code zum Selbstbuchen hinten. Kommt garantiert wieder.',
    points: ['Visitenkartenformat', 'Beschreibbare Vorderseite', 'Paket mit 250 Stück'],
    price: 'ab 29 €',
    priceNote: 'pro 250 Stück',
    icon: CalendarCheck,
    glow: '#B48CC8',
  },
  {
    id: 'nfc-tresen-sticker',
    name: 'NFC-Sticker für den Tresen',
    category: 'Bewertungen',
    description: 'Klein, rund und unauffällig an der Kasse. Ein Tipp mit dem Handy öffnet deine Bewertungsseite.',
    points: ['5 cm Durchmesser', 'Klebt auf Glas, Holz und Kunststoff', 'Im 3er-Set'],
    price: 'ab 24 €',
    priceNote: 'pro 3er-Set',
    icon: Nfc,
    glow: '#00B0FF',
  },
  {
    id: 'tuerschild',
    name: 'Türschild mit Öffnungszeiten',
    category: 'Online buchen',
    description: 'Öffnungszeiten auf einen Blick plus QR-Code: Wer vor verschlossener Tür steht, bucht trotzdem.',
    points: ['Alu-Dibond, wetterfest', 'Format A5 oder A4', 'Mit Klebepads oder Bohrlöchern'],
    price: 'ab 49 €',
    priceNote: 'pro Schild',
    icon: DoorOpen,
    glow: '#FF6D00',
  },
]

// ─── FadeIn helper ───────────────────────────────────────────────────────────

function FadeIn({ children, delay = 0, style }: { children: React.ReactNode; delay?: number; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  const reduceMotion = useReducedMotion()

  return (
    <motion.div
      ref={ref}
      initial={reduceMotion ? {} : { opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={style}
    >
      {children}
    </motion.div>
  )
}

// ─── Hintergrund ─────────────────────────────────────────────────────────────

function ShopBackground() {
  return (
    <div aria-hidden style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: '#0E0B1E' }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: [
          'radial-gradient(ellipse 60% 45% at 15% 0%, rgba(255,109,0,0.16) 0%, transparent 70%)',
          'radial-gradient(ellipse 50% 40% at 90% 15%, rgba(0,176,255,0.10) 0%, transparent 70%)',
          'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(101,79,111,0.22) 0%, transparent 70%)',
        ].join(', '),
      }} />
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.06, mixBlendMode: 'overlay' }}>
        <filter id="shop-grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="3" stitchTiles="stitch" />
        </filter>
        <rect width="100%" height="100%" filter="url(#shop-grain)" />
      </svg>
    </div>
  )
}

// ─── Header ──────────────────────────────────────────────────────────────────

function ShopHeader() {
  const isMobile = useWindowWidth() < 768

  return (
    <section style={{
      position: 'relative',
      zIndex: 1,
      textAlign: 'center',
      padding: isMobile ? '112px 20px 48px' : '160px 32px 72px',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ maxWidth: 760, margin: '0 auto' }}
      >
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: 'rgba(255,109,0,0.1)',
          border: '1px solid rgba(255,109,0,0.25)',
          borderRadius: 100,
          padding: '6px 16px',
          marginBottom: 28,
        }}>
          <QrCode size={14} color="#FF6D00" />
          <span style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 12, fontWeight: 600,
            color: '#FF9A00',
            letterSpacing: '0.04em',
          }}>
            Candlear Shop
          </span>
        </div>

        <h1 style={{
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontWeight: 800,
          fontSize: 'clamp(40px, 7vw, 68px)',
          lineHeight: 1.05,
          letterSpacing: '-0.025em',
          margin: 0,
        }}>
          <span style={{ color: 'white', display: 'block' }}>Mehr Kunden.</span>
          <span style={{
            display: 'block',
            background: 'linear-gradient(135deg, #FF6D00 0%, #FF9A00 50%, #FFB800 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            Zum Anfassen.
          </span>
        </h1>

        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: isMobile ? 16 : 18,
          lineHeight: 1.65,
          color: 'rgba(232,234,246,0.6)',
          maxWidth: 560,
          margin: '24px auto 0',
        }}>
          Karten, Aufsteller und Sticker für deinen Laden. Sie bringen Google-Bewertungen
          und Online-Buchungen – direkt vor Ort, wo deine Kunden sowieso schon sind.
        </p>
      </motion.div>
    </section>
  )
}

// ─── Produktkarte ────────────────────────────────────────────────────────────

function ProductCard({ product }: { product: Product }) {
  const [hovered, setHovered] = useState(false)
  const Icon = product.icon

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${hovered ? `${product.glow}50` : 'rgba(101,79,111,0.28)'}`,
        borderRadius: 20,
        overflow: 'hidden',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered
          ? `0 16px 48px rgba(0,0,0,0.3), 0 0 0 1px ${product.glow}20`
          : '0 2px 16px rgba(0,0,0,0.15)',
        transition: 'transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
        backdropFilter: 'blur(8px)',
      }}
    >
      <ProductVisual product={product} hovered={hovered} />

      <div style={{ padding: '24px 28px 28px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 11, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          color: product.glow,
          margin: '0 0 8px',
        }}>
          {product.category}
        </p>
        <h3 style={{
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontSize: 20, fontWeight: 700,
          color: 'white', margin: '0 0 10px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Icon size={18} color={product.glow} style={{ flexShrink: 0 }} />
          {product.name}
        </h3>
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 14, lineHeight: 1.65,
          color: 'rgba(232,234,246,0.55)',
          margin: '0 0 18px',
        }}>
          {product.description}
        </p>

        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'grid', gap: 8 }}>
          {product.points.map((point) => (
            <li key={point} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              color: 'rgba(232,234,246,0.75)',
            }}>
              <Check size={14} color={product.glow} style={{ flexShrink: 0 }} />
              {point}
            </li>
          ))}
        </ul>

        <div style={{
          marginTop: 'auto',
          paddingTop: 18,
          borderTop: '1px solid rgba(101,79,111,0.22)',
          display: 'flex', alignItems: 'baseline', gap: 8,
        }}>
          <span style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontSize: 24, fontWeight: 800,
            color: 'white',
            letterSpacing: '-0.01em',
          }}>
            {product.price}
          </span>
          <span style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            color: 'rgba(232,234,246,0.4)',
          }}>
            {product.priceNote}
          </span>
        </div>
      </div>
    </article>
  )
}

// Gezeichnete Produktvorschau statt Foto: eine kleine Karte mit QR-Raster.
function ProductVisual({ product, hovered }: { product: Product; hovered: boolean }) {
  const Icon = product.icon

  return (
    <div style={{
      position: 'relative',
      height: 168,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `radial-gradient(circle at 50% 120%, ${product.glow}30 0%, transparent 65%), rgba(14,11,30,0.6)`,
      borderBottom: '1px solid rgba(101,79,111,0.2)',
      overflow: 'hidden',
    }}>
      {product.badge && (
        <span style={{
          position: 'absolute', top: 14, right: 14,
          fontFamily: 'Inter, sans-serif',
          fontSize: 11, fontWeight: 700,
          color: '#FF9A00',
          background: 'rgba(255,109,0,0.12)',
          border: '1px solid rgba(255,109,0,0.3)',
          borderRadius: 100,
          padding: '4px 10px',
        }}>
          {product.badge}
        </span>
      )}

      <div style={{
        width: 168, height: 104,
        borderRadius: 12,
        background: 'linear-gradient(145deg, #1E1838 0%, #141029 100%)',
        border: `1px solid ${product.glow}40`,
        boxShadow: `0 18px 40px rgba(0,0,0,0.45), 0 4px 18px ${product.glow}25`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px',
        transform: hovered ? 'rotate(-4deg) scale(1.04)' : 'rotate(-7deg) scale(1)',
        transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 9,
            background: `${product.glow}20`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={16} color={product.glow} />
          </div>
          <div style={{ width: 56, height: 5, borderRadius: 3, background: 'rgba(232,234,246,0.5)' }} />
          <div style={{ width: 38, height: 5, borderRadius: 3, background: 'rgba(232,234,246,0.22)' }} />
        </div>
        <QrPattern seed={product.id} />
      </div>
    </div>
  )
}

// Deterministisches Pseudo-QR-Muster pro Produkt (nur Deko, nicht scannbar).
const QR_SIZE = 7

function qrCells(seed: string): boolean[] {
  let h = 0
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  const cells: boolean[] = []
  for (let i = 0; i < QR_SIZE * QR_SIZE; i++) {
    const x = i % QR_SIZE
    const y = Math.floor(i / QR_SIZE)
    const corner = (x < 2 && y < 2) || (x > 4 && y < 2) || (x < 2 && y > 4)
    h = (h * 1103515245 + 12345) >>> 0
    cells.push(corner || (h >> 16) % 2 === 0)
  }
  return cells
}

function QrPattern({ seed }: { seed: string }) {
  const size = QR_SIZE
  const cells = qrCells(seed)

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${size}, 6px)`,
      gap: 1.5,
      padding: 6,
      background: 'rgba(232,234,246,0.92)',
      borderRadius: 6,
    }}>
      {cells.map((on, i) => (
        <span key={i} style={{ width: 6, height: 6, borderRadius: 1, background: on ? '#0E0B1E' : 'transparent' }} />
      ))}
    </div>
  )
}

// ─── Produktliste ────────────────────────────────────────────────────────────

function ProductGrid() {
  const width = useWindowWidth()
  const columns = width < 700 ? 1 : width < 1024 ? 2 : 3

  return (
    <section style={{
      position: 'relative',
      zIndex: 1,
      padding: width < 768 ? '0 20px 64px' : '0 32px 100px',
    }}>
      <div style={{
        maxWidth: 1100,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: 24,
      }}>
        {PRODUCTS.map((product, i) => (
          <FadeIn key={product.id} delay={(i % columns) * 0.08} style={{ height: '100%' }}>
            <ProductCard product={product} />
          </FadeIn>
        ))}
      </div>
    </section>
  )
}

// ─── Hinweis: Bestellung ─────────────────────────────────────────────────────

function OrderNotice() {
  const isMobile = useWindowWidth() < 768

  return (
    <section style={{
      position: 'relative',
      zIndex: 1,
      background: 'linear-gradient(to bottom, rgba(18,15,34,0.98), rgba(26,16,64,0.98))',
      padding: isMobile ? '64px 20px 0' : '96px 32px 0',
      borderTop: '1px solid rgba(101,79,111,0.18)',
    }}>
      <FadeIn style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
        <div style={{
          width: 48, height: 48,
          margin: '0 auto 20px',
          borderRadius: 14,
          background: 'rgba(0,176,255,0.12)',
          border: '1px solid rgba(0,176,255,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Info size={22} color="#00B0FF" />
        </div>
        <h2 style={{
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontSize: 'clamp(26px, 4vw, 36px)',
          fontWeight: 700,
          color: '#E8EAF6',
          margin: '0 0 14px',
          letterSpacing: '-0.02em',
        }}>
          Online bestellen kommt bald.
        </h2>
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 16, lineHeight: 1.65,
          color: 'rgba(232,234,246,0.55)',
          margin: 0,
        }}>
          Die Preise sind Richtpreise. Du willst schon jetzt etwas für deinen Laden?
          Schreib uns an{' '}
          <a
            href="mailto:support@candlear.de"
            style={{ color: '#FF9A00', textDecoration: 'none', fontWeight: 600, borderBottom: '1px solid rgba(255,154,0,0.4)' }}
          >
            support@candlear.de
          </a>
          .
        </p>
      </FadeIn>

      <MarketingFooter />
    </section>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ShopPage() {
  useEffect(() => {
    const previous = document.title
    document.title = 'Shop – Candlear'
    return () => { document.title = previous }
  }, [])

  return (
    <div style={{ position: 'relative', background: '#0E0B1E', minHeight: '100vh', overflowX: 'hidden' }}>
      <ShopBackground />
      <MarketingNav />
      <ShopHeader />
      <ProductGrid />
      <OrderNotice />
    </div>
  )
}
